import { DateTime } from 'luxon';
import React, { Fragment, type JSX } from 'react';
import { useSelector } from 'react-redux';

import { profileSelectors } from 'app-components/flux/profile';
import type { Profile } from 'app-components/flux/profile/profileTypes';
import { subscriptionsSelectors } from 'app-components/flux/subscriptions';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

import simplifiLogoTextQBP from 'assets/logos/simplifi-qbp-text-dark.svg';
import simplifiLogoTextWhiteQBP from 'assets/logos/simplifi-qbp-text-white.svg';
import simplifiLogoText from 'assets/logos/simplifi-text-dark.svg';
import simplifiLogoTextWhite from 'assets/logos/simplifi-text-white.svg';
import {
  DetailField,
  DetailFieldEmptyValue,
  DetailFieldRow,
  DetailFieldValue,
} from 'containers/Settings/ChildPages/General/DetailField';
import { SettingsFeatureCard } from 'containers/Settings/ChildPages/General/SettingsFeatureCard';

const getFullName = (profile: Profile | undefined): string => {
  if (!profile) {
    return '';
  }
  if (profile.firstName && profile.lastName) {
    return `${profile.firstName} ${profile.lastName}`;
  }
  if (profile.primaryAddress && profile.primaryAddress.fullName) {
    return profile.primaryAddress.fullName;
  }
  return '';
};

const getSubscriptionLogoSrc = (tierUriName: string, isDarkMode: boolean): string => {
  if (tierUriName === 'acme-hab') {
    return isDarkMode ? simplifiLogoTextWhiteQBP : simplifiLogoTextQBP;
  }
  return isDarkMode ? simplifiLogoTextWhite : simplifiLogoText;
};

const useStyles = makeStyles()((theme) => ({
  greenDot: {
    display: 'inline-block',
    backgroundColor: theme.palette.success.main,
    height: 12,
    width: 12,
    borderRadius: 6,
    marginRight: 6,
  },
  redDot: {
    display: 'inline-block',
    backgroundColor: theme.palette.error.main,
    height: 12,
    width: 12,
    borderRadius: 6,
    marginRight: 6,
  },
}));

export type AccountDetailsCardProps = {
  onAddSubscription: () => void;
};

const AccountDetailsCard = ({ onAddSubscription }: AccountDetailsCardProps): JSX.Element => {
  const { classes } = useStyles();
  const theme = useTheme();

  const profile = useSelector(profileSelectors.getProfile);
  const subscription = useSelector(subscriptionsSelectors.getActiveSubscription);
  const displaySubscription = useSelector(subscriptionsSelectors.selectActiveOrLastSimplifiSubscription);
  const productSubscriptions = useSelector(subscriptionsSelectors.getProductSubscriptions);

  const subscriptionLogoSrc = displaySubscription?.tierUriName
    ? getSubscriptionLogoSrc(displaySubscription.tierUriName, theme.palette.mode === 'dark')
    : null;

  const fullName = getFullName(profile);

  const nameContent = <DetailFieldValue id='user-details-name'>{fullName || '-'}</DetailFieldValue>;

  const quickenIdContent = <DetailFieldValue id='user-details-id'>{profile?.username}</DetailFieldValue>;

  const primaryPhoneContent = profile?.primaryPhone?.number ? (
    <Stack direction='row' gap={1} alignItems='center'>
      <DetailFieldValue id='user-details-phone'>{profile.primaryPhone.number}</DetailFieldValue>
      {profile.primaryPhone.verified ? (
        <Chip color='success' variant='tonal' size='small' label='Verified' />
      ) : (
        <Chip variant='tonal' size='small' label='Not Verified' />
      )}
    </Stack>
  ) : (
    <DetailFieldEmptyValue />
  );

  const primaryAddressContent = profile?.primaryAddress ? (
    <DetailFieldValue>
      {[
        [profile.primaryAddress.line1, profile.primaryAddress.line2].filter(Boolean).join(', '),
        [profile.primaryAddress.city, profile.primaryAddress.state, profile.primaryAddress.zipCode]
          .filter(Boolean)
          .join(', '),
        profile.primaryAddress.country,
      ]
        .filter(Boolean)
        .map((line) => (
          <Fragment key={line}>
            {line}
            <br />
          </Fragment>
        ))}
    </DetailFieldValue>
  ) : (
    <DetailFieldEmptyValue />
  );

  return (
    <SettingsFeatureCard id='account-details' title='Personal details' subtitle='Shared across all Quicken products'>
      <Stack gap={3}>
        <Paper elevation={0} sx={{ padding: 0 }}>
          <Stack gap={2}>
            <DetailField label='Name'>{nameContent}</DetailField>
            <DetailField label='Quicken ID'>{quickenIdContent}</DetailField>
            <DetailFieldRow>
              <DetailField label='Primary phone'>{primaryPhoneContent}</DetailField>
              <DetailField label='Primary address'>{primaryAddressContent}</DetailField>
            </DetailFieldRow>
          </Stack>
        </Paper>
        <Alert severity='info'>
          You can manage this information for all Quicken products at <b>myaccount.quicken.com</b>.
        </Alert>
        <Stack gap={2}>
          <Divider />
          <Paper elevation={0} sx={{ padding: 0 }}>
            <Stack gap={3} alignItems='flex-start'>
              <Typography variant='bodyMedium'>Subscription</Typography>
              {subscriptionLogoSrc && (
                <img
                  height={40}
                  src={subscriptionLogoSrc}
                  alt={displaySubscription?.productName || displaySubscription?.tierName || 'Quicken Simplifi'}
                />
              )}
              {Boolean(productSubscriptions?.size) && (
                <DetailFieldRow>
                  <DetailField label='Subscription status'>
                    <DetailFieldValue>
                      {subscription ? (
                        <>
                          <span className={classes.greenDot} /> Active
                        </>
                      ) : (
                        <>
                          <span className={classes.redDot} /> Expired
                        </>
                      )}
                    </DetailFieldValue>
                  </DetailField>
                  {subscription && (
                    <DetailField label='Auto renew'>
                      <DetailFieldValue>{subscription.autoRenew ? 'On' : 'Off'}</DetailFieldValue>
                    </DetailField>
                  )}
                </DetailFieldRow>
              )}
              {subscription && (
                <DetailField label={subscription.autoRenew ? 'Renews on' : 'Expires on'}>
                  <DetailFieldValue>
                    {subscription.expireOn ? DateTime.fromISO(subscription.expireOn).toFormat('LLL dd, yyyy') : '-'}
                  </DetailFieldValue>
                </DetailField>
              )}
              {!subscription && (
                <Button id='add-subscription' onClick={onAddSubscription} variant='outlined'>
                  Add Subscription
                </Button>
              )}
            </Stack>
          </Paper>
        </Stack>
      </Stack>
    </SettingsFeatureCard>
  );
};

export default AccountDetailsCard;
